import styled from "styled-components";
import { useEffect, useState } from 'react';
import execute from "hooks/execute";
import { protectedResources } from 'config/msalConfig';
import { Form, Select } from "antd";

function parseModuleShortNames(modulesValue) {
    if (!modulesValue || typeof modulesValue !== 'string') {
        return [];
    }

    return modulesValue
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
}

function CustomerRoleFilter({ setOrganizationId, setCustomerId, handleSearch, setAvailableModules }) {
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(true);
    const [organizations, setOrganizations] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [orgModules, setOrgModules] = useState([]);

    const buildCustomerScopedModules = (selectedCustomerId, allOrgModules, allCustomers) => {
        const selectedCustomer = allCustomers.find((customer) => customer.value === selectedCustomerId);
        if (!selectedCustomer) {
            setAvailableModules(allOrgModules);
            return;
        }

        const customerShortNames = parseModuleShortNames(selectedCustomer.modules);
        if (customerShortNames.length === 0) {
            setAvailableModules(allOrgModules);
            return;
        }

        const filteredModules = allOrgModules.filter((module) => customerShortNames.includes(module.shortName));
        setAvailableModules(filteredModules);
    };

    const fetchCustomers = async (orgId) => {
        const response = await execute('GET', `${protectedResources.api.baseEndPoint}/organizations/${orgId}/customers`);
        if (!response?.success || !Array.isArray(response.data)) {
            setCustomers([]);
            return [];
        }

        const mappedCustomers = response.data.map((item) => ({
            value: item.Customer_ID?.toString(),
            label: item.CustEmail,
            modules: item.Modules || ''
        }));

        setCustomers(mappedCustomers);
        return mappedCustomers;
    };

    const fetchModulesByOrg = async (orgId) => {
        const [modulesResponse, organizationsResponse] = await Promise.all([
            execute('GET', `${protectedResources.api.baseEndPoint}/modules`),
            execute('GET', `${protectedResources.api.baseEndPoint}/organizations`)
        ]);

        if (!modulesResponse?.success || !organizationsResponse?.success) {
            setOrgModules([]);
            setAvailableModules([]);
            return [];
        }

        const selectedOrg = organizationsResponse.data.find((org) => String(org.Org_ID) === String(orgId));
        const orgShortNames = parseModuleShortNames(selectedOrg?.Modules);

        const filteredModules = modulesResponse.data
            .filter((module) => orgShortNames.length === 0 || orgShortNames.includes(module.ShortName))
            .map((module) => ({
                id: module.Id,
                shortName: module.ShortName,
                moduleName: module.ModuleName,
                label: module.ShortName,
                value: module.ShortName
            }));

        setOrgModules(filteredModules);
        setAvailableModules(filteredModules);
        return filteredModules;
    };

    const fetchOrganizations = async () => {
        try {
            setLoading(true);
            const response = await execute('GET', `${protectedResources.api.baseEndPoint}/organizations`);
            if (!response?.success || !Array.isArray(response.data) || response.data.length === 0) {
                setOrganizations([]);
                return;
            }

            const mappedOrganizations = response.data.map((item) => ({
                value: String(item.Org_ID),
                label: item.Org_Name
            }));
            setOrganizations(mappedOrganizations);

            const defaultOrgId = mappedOrganizations[0].value;
            setOrganizationId(defaultOrgId);

            const [fetchedCustomers, fetchedModules] = await Promise.all([
                fetchCustomers(defaultOrgId),
                fetchModulesByOrg(defaultOrgId)
            ]);

            const defaultCustomerId = fetchedCustomers[0]?.value || undefined;
            if (defaultCustomerId) {
                setCustomerId(defaultCustomerId);
                buildCustomerScopedModules(defaultCustomerId, fetchedModules, fetchedCustomers);
            } else {
                setCustomerId(null);
                setAvailableModules(fetchedModules);
            }

            form.setFieldsValue({
                organization_id: defaultOrgId,
                customer_id: defaultCustomerId
            });

            if (defaultCustomerId) {
                form.submit();
            }
        } catch (error) {
            console.error('Failed to fetch customer-role filters:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOrganizationChange = async (value) => {
        setOrganizationId(value);
        setLoading(true);

        try {
            const [fetchedCustomers, fetchedModules] = await Promise.all([
                fetchCustomers(value),
                fetchModulesByOrg(value)
            ]);

            const nextCustomerId = fetchedCustomers[0]?.value;
            setCustomerId(nextCustomerId || null);

            form.setFieldsValue({
                organization_id: value,
                customer_id: nextCustomerId
            });

            if (nextCustomerId) {
                buildCustomerScopedModules(nextCustomerId, fetchedModules, fetchedCustomers);
                form.submit();
            } else {
                setAvailableModules(fetchedModules);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerChange = (value) => {
        setCustomerId(value);
        form.setFieldsValue({ customer_id: value });
        buildCustomerScopedModules(value, orgModules, customers);
        form.submit();
    };

    useEffect(() => {
        fetchOrganizations();
    }, []);

    return (
        <SearchParameterContainer>
            <SearchParameterTitle>Search Parameter</SearchParameterTitle>
            <Form
                layout="vertical"
                onFinish={handleSearch}
                form={form}
                initialValues={{ layout: "vertical" }}
                style={{ display: "flex", gap: "16px", alignItems: "flex-end" }}
            >
                <Form.Item name="organization_id" label="Organization" style={{ flex: 1 }}>
                    <Select
                        showSearch
                        loading={loading}
                        placeholder="Select Organization"
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={organizations}
                        onChange={handleOrganizationChange}
                    />
                </Form.Item>

                <Form.Item name="customer_id" label="Customer" style={{ flex: 1 }}>
                    <Select
                        showSearch
                        loading={loading}
                        placeholder="Select Customer"
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={customers}
                        onChange={handleCustomerChange}
                    />
                </Form.Item>
            </Form>
        </SearchParameterContainer>
    );
}

const SearchParameterContainer = styled.section`
    padding: 20px;
    background-color: #f8f8f8;
    border-radius: 0px;
    margin: 20px;
    width: 100%;
    margin-left: 0;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    margin-top:-20px;
`;

const SearchParameterTitle = styled.h2`
    color: var(--bicollab-primary-color-blue-900, #002c57);
    letter-spacing: 0.4px;
    font: 500 16px/100% Ubuntu, sans-serif;

    @media (max-width: 991px) {
        max-width: 100%;
    }
`;

export default CustomerRoleFilter;
